import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

// Import local banner images
import banner1 from '../images/banner1.jpg';
import banner2 from '../images/banner2.jpg';
import banner3 from '../images/banner3.jpg';

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);

const slides = [
  {
    image: banner1,
    subtitle: 'INTEGRATED SOLUTIONS',
    title: 'INDUSTRIAL MAINTENANCE',
    description: 'Premier provider of high-quality bearings, tools, and spare parts for all industrial needs.',
  },
  {
    image: banner2,
    subtitle: 'QUALITY ASSURANCE',
    title: 'AUTO PARTS & EQUIPMENT',
    description: 'Comprehensive range of automotive parts and industrial equipment from top global brands.',
  },
  {
    image: banner3,
    subtitle: 'TRUSTED PARTNER',
    title: 'SUPPLYING SAUDI ARABIA',
    description: 'Your reliable partner for industrial excellence in Riyadh, Dammam, and beyond since 2012.',
  },
];

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const timer = setInterval(() => {
      nextSlide();
    }, 6000);
    return () => clearInterval(timer);
  }, [currentSlide]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <Box
      id="home"
      sx={{
        height: '100vh',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: '#000',
      }}
    >
      {/* Background Slides */}
      <AnimatePresence mode="wait">
        <MotionBox
          key={currentSlide}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${slides[currentSlide].image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {/* Gradient Overlay */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: theme.palette.mode === 'dark' 
                ? 'linear-gradient(90deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.3) 100%)'
                : 'linear-gradient(90deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0.4) 100%)',
            }}
          />
        </MotionBox>
      </AnimatePresence>

      {/* Main Content */}
      <Container
        maxWidth="xl"
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          zIndex: 5,
          pt: { xs: 8, md: 0 },
        }}
      >
        <Box sx={{ maxWidth: { xs: '100%', md: 850 }, pl: { xs: 0, md: 4 } }}>
          <AnimatePresence mode="wait">
            <Box key={currentSlide}>
              <MotionBox
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}
              >
                <Box sx={{ width: 40, height: 3, bgcolor: '#e57373' }} />
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: '#e57373',
                    fontWeight: 700,
                    letterSpacing: 4,
                    fontFamily: '"Rajdhani", sans-serif',
                    textTransform: 'uppercase',
                  }}
                >
                  {slides[currentSlide].subtitle}
                </Typography>
              </MotionBox>

              <MotionTypography
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 30 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                variant="h1"
                sx={{
                  color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                  fontWeight: 800,
                  lineHeight: { xs: 1.1, md: 1 },
                  mb: 3,
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '5rem', lg: '6rem' },
                  fontFamily: '"Orbitron", sans-serif',
                  textTransform: 'uppercase',
                  textShadow: theme.palette.mode === 'dark' ? '0 10px 30px rgba(0,0,0,0.5)' : 'none',
                }}
              >
                {slides[currentSlide].title}
              </MotionTypography>

              <MotionTypography
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                variant="h6"
                sx={{
                  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                  maxWidth: 600,
                  mb: 5,
                  fontWeight: 300,
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  lineHeight: 1.6,
                }}
              >
                {slides[currentSlide].description}
              </MotionTypography>

              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    onClick={() => {
                        const element = document.querySelector('#products');
                        if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                    variant="contained"
                    size="large"
                    endIcon={<KeyboardArrowRightIcon />}
                    sx={{
                      bgcolor: '#e57373',
                      color: 'white',
                      px: 5,
                      py: 1.8,
                      borderRadius: 0,
                      fontSize: '1rem',
                      fontWeight: 600,
                      letterSpacing: 1,
                      border: '1px solid #e57373',
                      '&:hover': {
                        bgcolor: 'transparent',
                        borderColor: '#e57373',
                        color: theme.palette.mode === 'dark' ? 'white' : '#e57373',
                      },
                    }}
                  >
                    EXPLORE PRODUCTS
                  </Button>
                  <Button
                    onClick={() => {
                        const element = document.querySelector('#contact');
                        if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                    variant="outlined"
                    size="large"
                    sx={{
                      color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                      borderColor: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                      px: 5,
                      py: 1.8,
                      borderRadius: 0,
                      fontSize: '1rem',
                      fontWeight: 600,
                      letterSpacing: 1,
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                        color: theme.palette.mode === 'dark' ? 'black' : 'white',
                        borderColor: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                      },
                    }}
                  >
                    CONTACT US
                  </Button>
                </Stack>
              </MotionBox>
            </Box>
          </AnimatePresence>
        </Box>
      </Container>

      {/* Navigation Controls */}
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: 30, md: 60 },
          right: { xs: 20, md: 60 },
          zIndex: 10,
          display: 'flex',
          gap: 2,
        }}
      >
        <IconButton
          onClick={prevSlide}
          sx={{
            color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
            border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.3)' : '1px solid rgba(0,0,0,0.2)',
            borderRadius: 0,
            p: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: '#e57373',
              borderColor: '#e57373',
              color: 'white',
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <IconButton
          onClick={nextSlide}
          sx={{
            color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
            border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.3)' : '1px solid rgba(0,0,0,0.2)',
            borderRadius: 0,
            p: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: '#e57373',
              borderColor: '#e57373',
              color: 'white',
            },
          }}
        >
          <ArrowForwardIcon />
        </IconButton>
      </Box>

      {/* Progress Indicators */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          display: 'flex',
          width: '100%',
          zIndex: 10,
        }}
      >
        {slides.map((_, index) => (
          <Box
            key={index}
            sx={{
              flex: 1,
              height: 4,
              bgcolor: currentSlide === index ? '#e57373' : 'rgba(255,255,255,0.1)',
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default Hero;
